import * as THREE from "three";

/** Octets par texel selon le format des données : les textures flottantes pèsent quatre fois plus. */
function bytesPerTexel(texture: THREE.Texture): number {
  const channel = texture.type === THREE.FloatType ? 4 : texture.type === THREE.HalfFloatType ? 2 : 1;
  return channel * 4;
}

function textureBytes(texture: THREE.Texture): number {
  const image = texture.image as { width?: number; height?: number; data?: ArrayBufferView } | undefined;
  if (image?.data) return image.data.byteLength;
  const texels = (image?.width ?? 0) * (image?.height ?? 0);
  // Une image réduite par paliers (mipmaps) en ajoute un tiers.
  return texels * bytesPerTexel(texture) * (texture.generateMipmaps ? 4 / 3 : 1);
}

/**
 * Ce que ces objets envoient au GPU, estimé : leurs géométries (lots instanciés compris) et les
 * textures de leurs matériaux, chacune comptée une fois, plus `extraTextures` qu'aucun matériau ne
 * montre directement. Une estimation : le pilote ajoute ses propres marges.
 */
export function sceneMemoryBytes(roots: THREE.Object3D[], extraTextures: THREE.Texture[] = []): number {
  const buffers = new Set<ArrayBufferLike>();
  const textures = new Set<THREE.Texture>(extraTextures);
  let bytes = 0;
  const addArray = (array: ArrayLike<number> & { buffer?: ArrayBufferLike; byteLength?: number } | undefined) => {
    if (!array?.buffer || buffers.has(array.buffer)) return;
    buffers.add(array.buffer);
    bytes += array.byteLength ?? 0;
  };
  for (const root of roots) {
    root.traverse((node) => {
      if (!(node instanceof THREE.Mesh)) return;
      const geometry = node.geometry as THREE.BufferGeometry;
      for (const attribute of Object.values(geometry.attributes)) addArray((attribute as THREE.BufferAttribute).array);
      addArray(geometry.index?.array);
      if (node instanceof THREE.InstancedMesh) {
        addArray(node.instanceMatrix.array);
        addArray(node.instanceColor?.array);
      }
      for (const material of [node.material].flat() as THREE.Material[]) {
        for (const value of Object.values(material)) if (value instanceof THREE.Texture) textures.add(value);
      }
    });
  }
  for (const texture of textures) bytes += textureBytes(texture);
  return bytes;
}
